// src/pages/Property/standaloneReviewPropertyDetails.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReviewPropertyDetails from './reviewPropertyDetails';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';
import useApiState from '../../Hooks/useApiState';
import UseAxios from '../../Api/http/http';
import { fetchPropertyDetail } from '../../Api/request/propertyDetail';
import CryptoJS from 'crypto-js';

const mapPropertyForReview = (raw = {}) => {
  const docs = raw.property_documents || {};

  return {
    // Basic information
    caption: raw.property_caption || raw.caption || '',
    type: raw.property_type || raw.type || '',
    subType: raw.property_sub_type || raw.subType || '',
    description: raw.property_description || raw.description || '',

    // Address details
    address1: raw.address_line1 || raw.address1 || '',
    address2: raw.address_line2 || raw.address2 || '',
    area: raw.area || '',
    taluk: raw.taluk || '',
    district: raw.district || '',
    pin: raw.postal_code || raw.pin || '',
    city: raw.city || '',
    state: raw.state || '',
    country: raw.country || '',
    jurisdiction: raw.jurisdiction || '',
    location: raw.location || { mapLink: '', latitude: '', longitude: '' },

    // Building/Land details
    constructionStage: raw.construction_stage || raw.constructionStage || '',
    carpetArea: raw.carpet_area || raw.carpetArea || null,
    builtUpSanctioned: raw.builtup_area_sanctioned || raw.builtUpSanctioned || null,
    builtUpActual: raw.builtup_area_actual || raw.builtUpActual || null,
    noOfFloors: raw.floor_count || raw.noOfFloors || null,
    noOfParking: raw.parking_count || raw.noOfParking || null,
    extent: raw.land_extent || raw.extent || null,
    unit: raw.unit || '',
    pattaExtent: raw.patta_land_extent || raw.pattaExtent || null,
    kumkiExtent: raw.kumki_land_extent || raw.kumkiExtent || null,
    boundaryType: raw.boundary_type || raw.boundaryType || '',
    roadAccess: raw.road_access === '1' || raw.road_access === true || raw.roadAccess === true,
    roadType: raw.road_type || raw.roadType || '',
    accessRoadWidth: raw.access_road_width || raw.accessRoadWidth || null,
    facing: Array.isArray(raw.site_facing_direction)
      ? raw.site_facing_direction
      : (typeof raw.site_facing_direction === 'string' && raw.site_facing_direction
        ? JSON.parse(raw.site_facing_direction)
        : (raw.facing || [])),
    waterSource: Array.isArray(raw.water_source)
      ? raw.water_source
      : (typeof raw.water_source === 'string' && raw.water_source
        ? JSON.parse(raw.water_source)
        : (raw.waterSource || [])),
    powerSupply: raw.power_supply === '1' || raw.power_supply === true || raw.powerSupply === true,
    sanctionedCapacity: raw.sanctioned_capacity || raw.sanctionedCapacity || null,
    sewageLine: raw.sewage_line === '1' || raw.sewage_line === true || raw.sewageLine === true,
    sewageType: raw.sewage_type || raw.sewageType || '',

    // Financial
    taxPaidUpTo: raw.tax_paid_upto || raw.taxPaidUpTo || null,
    price: raw.purchase_price || raw.price || null,
    purchaseDate: raw.purchase_date || raw.purchaseDate || null,
    estimatedValue: raw.estimated_current_value || raw.estimatedValue || null,

    // Contact
    contactName: raw.owner_name || raw.contactName || null,
    phoneNumber: raw.owner_phone_number || raw.phoneNumber || null,
    email: raw.owner_email_id || raw.email || null,
    caretakerIsOwner: raw.caretaker_is_owner === '1' || raw.caretaker_is_owner === true || raw.caretakerIsOwner === true,
    caretakerName: raw.caretaker_name || raw.caretakerName || null,
    caretakerPhoneNumber: raw.caretaker_phone_number || raw.caretakerPhoneNumber || null,
    caretakerEmail: raw.caretaker_email_id || raw.caretakerEmail || null,
    status: raw.status || 'Draft',
    listingType: raw.listing_type || raw.listingType || '',

    // Media + documents
    images: raw.property_images || raw.images || [],
    videos: raw.property_videos || raw.videos || [],
    property_images: raw.property_images || [],
    property_videos: raw.property_videos || [],
    property_documents: docs,
    receipts: docs.tax_receipt || [],

    // Document buckets used by review screen
    'Tax Receipt': docs.tax_receipt || [],
    'Sale Deed': docs.sale_deed || [],
    'e-Khata': docs.khata || [],
    'FMB / Nakshe': docs.fmb_nakshe || [],
    'Conversion Order/Copy': docs.conversion_order_copy || [],
    'EC': docs.ec || [],
    'RTC': docs.rtc || [],
    'Mutation': docs.mutation || [],
    'Occupancy Certificate': docs.occupancy_certificate || [],
    'Original Mother Deed': docs.original_mother_deed || docs.mother_deed || [],
    'Building Licence': docs.building_licence || [],
    'CRZ NOC': docs.crz_noc || [],
    'RERA Certificate': docs.rera_certificate || [],
    'Pollution Control Board': docs.pollution_control_board || [],
    'Fire Dept Noc': docs.fire_dept_noc || [],
    'Airport Clearance': docs.airport_clearance || [],
    'Sanction Plan Of Building': docs.sanction_plan_building || [],
    'Bank documents (if loans)': docs.bank_loan_documents || [],
    'Legal documents (if litigation)': docs.legal_documents || [],
    'Electricity Bill': docs.electricity_bill || [],
    'Rent agreements': docs.rent_agreements || [],
    'Survey settlement records': docs.survey_settlement_records || [],
    'Possession certificate': docs.possession_certificate || [],
    'NOC from society builder': docs.noc_from_society_builder || [],
    'Insurance documents': docs.insurance_documents || [],
  };
};

const StandaloneReviewPropertyDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const { isLoading, error, fetchData } = useApiState();

  useEffect(() => {
    const loadPropertyDetails = async () => {
      try {
        // Get encrypted property ID from URL
        const encryptedId = searchParams.get('id');

        if (!encryptedId) {
          alert('Property ID is missing from URL');
          navigate('/property-list');
          return;
        }

        // Decrypt the property ID
        let decryptedId;
        try {
          // Decrypt the URL-encoded encrypted string
          const decodedEncryptedId = decodeURIComponent(encryptedId);
          const bytes = CryptoJS.AES.decrypt(decodedEncryptedId, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || 'a6d36c7f9478b4b169778a410d7a2287e0969f1a73b0e0b62552c28915b14aa5');
          decryptedId = bytes.toString(CryptoJS.enc.Utf8);

          if (!decryptedId) {
            throw new Error('Invalid encrypted ID');
          }
        } catch (decryptError) {
          console.error('Failed to decrypt property ID:', decryptError);
          alert('Invalid property ID in URL');
          navigate('/property-list');
          return;
        }

        console.log('Decrypted property ID:', decryptedId);
        setPropertyId(decryptedId);

        // Fetch property details using the decrypted ID
        const response = await fetchData(() => UseAxios(fetchPropertyDetail({
          property_id: decryptedId,
        })), {
          successMessage: 'Property details loaded',
        });

        if (response && response.data && response.data.data) {
          console.log('Property details fetched successfully');
          console.log('Complete property data:', response.data.data);
          setPropertyData(mapPropertyForReview(response.data.data));
        } else {
          throw new Error('Failed to fetch property details');
        }
      } catch (fetchError) {
        console.error('Failed to load property details:', fetchError);
        alert(`Failed to load property details: ${fetchError.message || 'Unknown error'}`);
        navigate('/property-list');
      }
    };

    loadPropertyDetails();
  }, [searchParams, navigate, fetchData]);

  const handleBackToDashboard = () => {
    // Close the tab or navigate back
    if (window.opener) {
      window.close();
    } else {
      navigate('/property-list');
    }
  };

  if (isLoading || !propertyData) {
    return <LoaderOverlay isLoading={true} />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Error Loading Property Details</h3>
        <p>{error}</p>
        <button onClick={handleBackToDashboard}>Go Back to Dashboard</button>
      </div>
    );
  }

  // Determine the review step based on property subtype
  const isVillaOrComplex = propertyData.subType === 'Villa' || propertyData.subType === 'Complex';
  const reviewStep = isVillaOrComplex ? 10 : 9;
  const totalSteps = isVillaOrComplex ? 10 : 9;

  return (
    <ReviewPropertyDetails
      propertyData={propertyData}
      step={reviewStep}
      totalSteps={totalSteps}
      systemId={propertyId}
      onBackToDashboard={handleBackToDashboard}
      prevStep={handleBackToDashboard}
      isView={true}
    />
  );
};

export default StandaloneReviewPropertyDetails;
